import classNames from 'classnames';
import { useAtom, useAtomValue } from 'jotai';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue } from 'recoil';
import { cn } from '../../common/utils';
import { Button } from '../../components/actions/Button';
import * as Icon from '../../components/icons';

import useUserPermissionFeatures from '../../permissions/roles/hooks/useUserPermissionFeatures';
import {
  actualityDataSetTableTypeSelected,
  actualitySelectedDocument,
} from '../store/actuality.state';
import { ActualityMainViewType } from '../store/actuality.types';
import {
  actualityMainViewTypeAtom,
  actualitySelectedMenuIdAtom,
  actualitySelectedTabIdAtom,
} from '../store/actuality.ui.state';

const FeedLayoutViewSwitch: React.FC = () => {
  const { t } = useTranslation('feed');
  const actuality = useRecoilValue(actualitySelectedDocument);
  const activityTabId = useAtomValue(actualitySelectedTabIdAtom);
  const activeMenuId = useAtomValue(actualitySelectedMenuIdAtom(actuality.id));
  const [viewAs, setViewAs] = useAtom(actualityMainViewTypeAtom);
  const [dataDisplayMode, setDataDisplayMode] = useRecoilState(
    actualityDataSetTableTypeSelected,
  );
  const visibleFeatures = useUserPermissionFeatures();

  const showLocationViews =
    activeMenuId === 'feed-menu-setup' ||
    (activeMenuId === 'feed-menu-activity' && activityTabId !== 'tab-feed-kpi');

  const showKPIView =
    activeMenuId === 'feed-menu-activity' && activityTabId === 'tab-feed-kpi';

  const showLayout =
    activityTabId !== 'tab-feed-kpi' &&
    (viewAs === 'layout' || viewAs === 'combined');
  const showKPI =
    activityTabId === 'tab-feed-kpi' &&
    (viewAs === 'layout' || viewAs === 'combined');
  const showTimeline = viewAs === 'timeline' || viewAs === 'combined';

  const hasLayout = visibleFeatures.find(
    (f: { id: string; visible: boolean }) => f.id === 'tab-feed-activity',
  )?.visible;

  const hasKPI = visibleFeatures.find(
    (f: { id: string; visible: boolean }) => f.id === 'tab-feed-kpi',
  )?.visible;

  const hasTimeline = visibleFeatures.find(
    (f: { id: string; visible: boolean }) =>
      f.id === 'feed-menu-setup' ||
      (activeMenuId === 'feed-menu-activity' &&
        activityTabId !== 'tab-feed-kpi'),
  )?.visible;

  const handleViewToggle = (
    value: Extract<ActualityMainViewType, 'layout' | 'timeline'>,
  ) => {
    if (viewAs === 'combined') {
      setViewAs(value === 'layout' ? 'timeline' : 'layout');
    }

    if (viewAs === 'layout') {
      setViewAs(value === 'layout' ? 'layout' : 'combined');
    }

    if (viewAs === 'timeline') {
      setViewAs(value === 'timeline' ? 'timeline' : 'combined');
    }
  };

  const handleDataViewToggle = () => {
    setDataDisplayMode(dataDisplayMode ? null : 'activity-feed');
  };

  const getButtonClassName = (isActive: boolean) => {
    return classNames(
      'h-7',
      'text-xxs',
      'flex items-center',
      'rounded-full',
      '!py-0.5 px-1',
      'focus:outline-none focus:ring-0',
      isActive
        ? 'bg-menu-secondary/90 text-menu-text/90 hover:text-menu-text hover:bg-menu-secondary'
        : 'bg-menu/30 hover:bg-menu-secondary',
    );
  };

  const buttonClassName = cn(
    'bg-app-panel-dark',
    'flex items-center justify-center',
    'gap-1',
    'rounded-full',
    'p-0.5',
  );

  return (
    <div className="mx-1 flex items-center justify-center gap-0.5 xl:mx-2 xl:gap-1">
      <div data-component="ButtonGroup" className={buttonClassName}>
        {hasLayout && showLocationViews && (
          <Button
            className={getButtonClassName(showLayout)}
            buttonSize="xs"
            label={t('Map')}
            onPress={() => handleViewToggle('layout')}
          />
        )}
        {hasKPI && showKPIView && (
          <Button
            className={getButtonClassName(showKPI)}
            buttonSize="xs"
            hasIconAfter
            label={t('KPI')}
            onPress={() => handleViewToggle('layout')}
          />
        )}
        {(showKPIView || showLocationViews) && (
          <Button
            className={getButtonClassName(showTimeline)}
            buttonSize="xs"
            label={t('Timeline')}
            onPress={() => handleViewToggle('timeline')}
          />
        )}
      </div>
      <div data-component="ButtonGroup" className={buttonClassName}>
        <Button
          className={getButtonClassName(!!dataDisplayMode)}
          buttonSize="xs"
          hasIconAfter
          onPress={handleDataViewToggle}
          buttonIcon={
            dataDisplayMode ? (
              <Icon.HideTable className="h-5 w-5 fill-current" />
            ) : (
              <Icon.ShowTable className="h-5 w-5 fill-current" />
            )
          }
        />
      </div>
    </div>
  );
};

export default FeedLayoutViewSwitch;
